import { useEffect, useRef, useState } from 'react'
import { ArrowRight, LockKeyhole, Mail, ShieldCheck } from 'lucide-react'
import { saidNothingOnPurpose } from '../../lib/act.js'
import {
  Button,
  Card,
  Field,
  Input,
  PasswordInput,
  Row,
  Spinner,
  Stack,
  StatusBanner,
  Text,
} from '../../components/index.js'
import { setAccessToken, type ApiErrorShape } from '../../store/axiosBaseQuery.js'
import {
  isChallenge,
  useCheckResetLinkQuery,
  useConfirmEnrolmentMutation,
  useConfirmMfaResetMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useSignInMutation,
  useRequestMfaResetMutation,
  useStartEnrolmentMutation,
  useVerifyCodeMutation,
} from './api/signIn.api.js'
import {
  CODE_WRONG,
  CREDENTIALS_WRONG,
  LINK_EXPIRED,
  PASSWORD_CHANGED_ANYWAY,
  PASSWORD_JUST_CHANGED,
  PASSWORDS_DIFFER,
  PASSWORD_RULE,
  RESET_SENT,
  RESET_SIGNS_YOU_IN,
  WHY_REFUSED,
  WHY_A_SECOND_STEP,
} from './core/constants.js'
import type { Enrolment, Session, SignInProps, SignInStep } from './core/types.js'
import { AdminSignInLayout } from './components/AdminSignInLayout.js'
import { CodeField } from './components/CodeField.js'
import { MfaEnrolment } from './components/MfaEnrolment.js'

/**
 * 1.10 — getting into the desk.
 *
 * **Nothing on this desk could be signed into.** The session slice, the token
 * holder in the axios layer and the lapsed-session hook were all written and
 * none of them was ever called: the rail said *"Signed out · not signed in yet"*
 * as a literal, the permission list was a hard-coded array of nine words, and
 * every screen talked to the API with no Authorization header at all. It worked
 * against nothing but a mock.
 *
 * Three steps, and the third only ever happens once per person:
 *
 * 1. Email and password. One message for both, whichever was wrong.
 * 2. Six digits from an authenticator app.
 * 3. **Or**, the first time, setting that app up — a QR, the key beside it for
 *    anybody whose camera will not read it, and ten recovery codes shown once.
 */
export function SignIn({ onSignedIn, resetToken, signedOutBecause }: SignInProps) {
  /* A reset link opens straight onto 1.11 rather than onto the password. */
  const [step, setStep] = useState<SignInStep>(resetToken ? 'choose' : 'password')
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  /** So Enter in the email box moves on rather than doing nothing. */
  const passwordRef = useRef<HTMLInputElement>(null)
  const [code, setCode] = useState('')
  /** Whether the set-up key is showing instead of being one press away. */
  const [typing, setTyping] = useState(false)
  /** Which of the three enrolment steps is on screen. */
  const [enrolStep, setEnrolStep] = useState(0)
  const [challenge, setChallenge] = useState('')
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null)
  const [refused, setRefused] = useState<string | null>(null)
  /** 1.11's two fields. */
  const [chosen, setChosen] = useState({ first: '', second: '' })
  /**
   * True from the moment `chooseNewPassword`'s reset call succeeds. Shown as a plain
   * confirmation on whatever screen comes next — the password is already changed by
   * then, and the client found that the code step which usually follows did not say so
   * (it looks the same whether this is an ordinary sign-in or the far end of a reset).
   */
  const [justResetPassword, setJustResetPassword] = useState(false)

  /**
   * 1.11 read before it is trusted, the way the phone screen already does
   * (`ResetPassword.tsx`, `checkResetLink`). An hour-old or already-used link used to
   * still draw this live-looking form — the desk found out only when Save was pressed,
   * which is one bad minute for a dead link and a second one for a client wondering,
   * after a *successful* reset, whether pressing the same email link again could undo
   * it. `keepUnusedDataFor: 0` on the query means a reload always asks again.
   */
  const link = useCheckResetLinkQuery({ token: resetToken ?? '' }, { skip: !resetToken })
  useEffect(() => {
    if (link.data && link.data.state !== 'valid') setStep('expired')
  }, [link.data])

  const [signIn, { isLoading: signingIn }] = useSignInMutation()
  const [verify, { isLoading: verifying }] = useVerifyCodeMutation()
  const [startEnrolment, { isLoading: starting }] = useStartEnrolmentMutation()
  const [confirmEnrolment, { isLoading: confirming }] = useConfirmEnrolmentMutation()
  const [requestReset, { isLoading: askingReset }] = useRequestMfaResetMutation()
  const [confirmReset, { isLoading: resettingMfa }] = useConfirmMfaResetMutation()
  const [forgot, { isLoading: sending }] = useForgotPasswordMutation()
  const [reset, { isLoading: resetting }] = useResetPasswordMutation()

  /**
   * The new password, and the step after it.
   *
   * **An administrator's reset does not end in a session.** It ends in a
   * second-factor challenge, exactly as signing in does — `resetPassword`
   * says why in as many words: *"anyone who could reach an admin's inbox
   * could have taken the account with no second factor at all."*
   *
   * This treated the answer as a session, read `access_token` off a
   * challenge, threw — and the `.catch` meant for a dead link reported a dead
   * link. The first administrator ever created on the live desk set their
   * password and was told it had expired. It had not; it had just been used.
   *
   * So the two halves are caught separately. Only the reset call's own
   * refusal means the link is spent. Once it has returned, **the password is
   * already changed** and nothing after it may say otherwise.
   */
  const chooseNewPassword = async () => {
    if (chosen.first.length < 8 || chosen.second === '') return
    /* Said under the fields rather than after a round trip. */
    if (chosen.first !== chosen.second) return setRefused(PASSWORDS_DIFFER)
    setRefused(null)

    let result: Awaited<ReturnType<ReturnType<typeof useResetPasswordMutation>[0]>>['data']
    try {
      result = await reset({ token: resetToken ?? '', password: chosen.first }).unwrap()
    } catch {
      /* 1.12: the link was older than an hour, or has already been used. */
      return setStep('expired')
    }

    // From here the password is saved, whatever happens next — so say so on
    // whatever screen this lands on, rather than leaving it to be inferred.
    setJustResetPassword(true)

    try {
      if (result === undefined) throw new Error('reset returned nothing')
      if (!isChallenge(result)) {
        // Not an administrator, or an administrator the server did not ask a
        // second step of. Either way there is a session and nothing to ask.
        accept(result)
        return
      }
      setChallenge(result.challenge)
      if (result.enrolled) {
        setStep('code')
        return
      }
      /* Never set one up — which is every first administrator, and was the
         exact case that broke. The QR and the recovery codes are issued now,
         and this is the only time they exist anywhere a person can see them. */
      const issued = await startEnrolment({ challenge: result.challenge }).unwrap()
      setEnrolment(issued)
      setStep('enrol')
    } catch (error) {
      /* Back to the password box with the truth: the new password works, and
         whatever failed was after it. Never `expired` — that sends somebody
         to fetch a second link they do not need. */
      setStep('password')
      setRefused(why(error, PASSWORD_CHANGED_ANYWAY))
    }
  }

  /** The token never touches Redux. See the note in `axiosBaseQuery`. */
  /**
   * In. The token where the HTTP layer reads it, and the session handed up.
   *
   * This used to be `setAccessToken(token); onSignedIn()` with `onSignedIn`
   * taking nothing — so the one fact that decides whether the desk is drawn
   * never left this screen, and a correct password and a correct code left
   * somebody looking at the sign-in form.
   */
  const accept = (session: Session) => {
    setAccessToken(session.access_token)
    onSignedIn(session)
  }

  /**
   * What actually went wrong, in the person's words rather than one sentence
   * for everything.
   *
   * The server's own message wins where it wrote one — the code step counts
   * down *"3 left"* and a constant here would throw the count away.
   */
  const why = (error: unknown, fallback: string) => {
    const e = error as { status?: number; code?: string; message?: string } | undefined
    const shape = e as ApiErrorShape | undefined
    if (shape?.code && shape.code in WHY_REFUSED) return WHY_REFUSED[shape.code]!
    if (shape?.code === 'CODE_INCORRECT' && shape.message) return shape.message
    return fallback
  }

  const submitPassword = async () => {
    setRefused(null)
    try {
      const result = await signIn({ identifier, password }).unwrap()
      if (!isChallenge(result)) {
        // A customer's account, or an administrator with no second step
        // required. Either way there is a session and nothing more to ask.
        accept(result)
        return
      }
      setChallenge(result.challenge)
      if (result.enrolled) {
        setStep('code')
        return
      }
      /* Never set one up. The QR and the codes are issued now, and this is the
         only time they exist anywhere a person can see them. */
      const issued = await startEnrolment({ challenge: result.challenge }).unwrap()
      setEnrolment(issued)
      setStep('enrol')
    } catch (error) {
      setRefused(why(error, CREDENTIALS_WRONG))
    }
  }

  /**
   * "Lost your authenticator" — email a code, then move to entering it.
   *
   * The challenge already in hand proves the password, so this is safe to offer here.
   * The answer is the same either way, so a stranger holding a stolen password learns
   * nothing about whether this account has a factor.
   */
  const startReset = async () => {
    setRefused(null)
    setCode('')
    setStep('reset')
    /* The route answers the same either way by design, and the step has a
       "Send the code again" button, so a hiccup is recoverable without a
       message here. See `saidNothingOnPurpose`. */
    saidNothingOnPurpose(requestReset({ challenge }))
  }

  /**
   * The email code is right: the old factor is cleared and a fresh one comes back, so
   * the screen goes to the QR and the new recovery codes exactly as a first run does.
   */
  const submitReset = async () => {
    setRefused(null)
    try {
      const fresh = await confirmReset({ challenge, code }).unwrap()
      setEnrolment(fresh)
      setEnrolStep(0)
      setTyping(false)
      setCode('')
      setStep('enrol')
    } catch (error) {
      setRefused(why(error, CODE_WRONG))
      setCode('')
    }
  }

  const submitCode = async () => {
    setRefused(null)
    try {
      const session =
        step === 'enrol'
          ? await confirmEnrolment({ challenge, code }).unwrap()
          : await verify({ challenge, code }).unwrap()
      accept(session)
    } catch (error) {
      setRefused(why(error, CODE_WRONG))
      setCode('')
    }
  }

  return (
    /**
     * This screen owns its frame instead of borrowing the generic layout used
     * by invitation and teaching links. Authentication behaviour stays here;
     * the visual panel and responsive shell stay in `AdminSignInLayout`.
     */
    <AdminSignInLayout>
      <Card
        padding="lg"
        elevation="raised"
        className="rounded-xl border-hairline bg-surface p-5 shadow-e2 sm:p-8"
      >
        <Stack gap={6}>
          {/* Why the last session ended, when it ended by itself. */}
          {signedOutBecause && step === 'password' && (
            <StatusBanner tone="attention">{signedOutBecause}</StatusBanner>
          )}

          {/*
            A real form, so Enter works and a password manager recognises it.
            
            Every step here was a `<Stack>` of boxes with ad-hoc key handlers
            on two of them, and the other two — "Send the link" and "Choose a
            new password" — swallowed Enter entirely. A browser will not
            offer to save or fill credentials it cannot see a form around,
            which on the one screen everybody meets first is the difference
            between signing in and typing a password from memory.
          */}
          {step === 'password' && (
            <Stack as="form" gap={5} onSubmit={onSubmit(() => void submitPassword())}>
              <Stack gap={2} className="mb-1">
                <Text variant="eyebrow" className="text-coral-text">
                  Secure team access
                </Text>
                <Text
                  as="h2"
                  variant="title"
                  className="text-[32px] leading-[1.15] tracking-[-0.035em]"
                >
                  Welcome back
                </Text>
                <Text variant="secondary" className="max-w-sm text-[15px] leading-6">
                  Sign in with your team credentials to open the High Impact administration desk.
                </Text>
              </Stack>
              <Field label="Email address">
                {({ id }) => (
                  <Input
                    id={id}
                    type="email"
                    /* The prototype's own placeholders. */
                    placeholder="Enter your email address"
                    autoComplete="username"
                    /**
                     * Focused on arrival.
                     *
                     * This is the first screen of every session and the only
                     * thing on it is this form, so landing with nothing
                     * focused means a click before a keystroke, every time.
                     * The one case where `autoFocus` is right rather than
                     * rude: there is nothing else here to take.
                     */
                    autoFocus
                    size="lg"
                    leading={<Mail aria-hidden />}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    /* Enter moved on from the password box and did nothing
                       here, so typing an address and pressing Enter — which
                       is what everybody does — was a keystroke into the void. */
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return
                      e.preventDefault()
                      passwordRef.current?.focus()
                    }}
                  />
                )}
              </Field>
              <Field label="Password">
                {({ id }) => (
                  <PasswordInput
                    id={id}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    size="lg"
                    leading={<LockKeyhole aria-hidden />}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    ref={passwordRef}
                  />
                )}
              </Field>
              {refused && <StatusBanner tone="error">{refused}</StatusBanner>}
              {/* Large and the full width of the card, which is what the board
                  draws — it is the only thing on the screen to press. */}
              <Button
                size="lg"
                full
                className="mt-1 h-12 shadow-[0_10px_24px_rgba(195,78,63,0.18)]"
                type="submit"
                loading={signingIn || starting}
                disabled={identifier.trim() === '' || password === ''}
                trailingIcon={<ArrowRight aria-hidden />}
              >
                Sign in
              </Button>
              {/* 1.10's second way out, which this screen did not have. The
                  component rather than a hand-rolled button: it was the one
                  control on the desk styled by its own class list. */}
              <Button
                variant="tertiary"
                size="sm"
                flush
                className="self-center"
                onClick={() => {
                  setRefused(null)
                  setStep('forgot')
                }}
              >
                Reset your password
              </Button>
              <div className="border-teal/15 bg-teal-soft flex items-start gap-3 rounded-lg border px-4 py-3.5">
                <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0 text-teal-text" />
                <Text variant="caption" className="leading-5 text-ink-2">
                  {WHY_A_SECOND_STEP}
                </Text>
              </div>
            </Stack>
          )}

          {step === 'forgot' && (
            <Stack
              as="form"
              gap={4}
              onSubmit={onSubmit(() => {
                if (identifier.trim() === '') return
                /* The same step either way — see `saidNothingOnPurpose` and
                   the banner it leads to. Whether the address has an account
                   is not something this screen may reveal. */
                saidNothingOnPurpose(forgot({ email: identifier.trim() }))
                setStep('sent')
              })}
            >
              <Text variant="body-strong" as="h2">
                Reset your password
              </Text>
              <Field label="Email address">
                {({ id }) => (
                  <Input
                    id={id}
                    type="email"
                    placeholder="Enter your email address"
                    autoComplete="username"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                  />
                )}
              </Field>
              <Button type="submit" loading={sending} disabled={identifier.trim() === ''}>
                Send the link
              </Button>
              <button
                type="button"
                className="self-start text-second text-action-text underline-offset-2 hover:underline"
                onClick={() => setStep('password')}
              >
                Back to sign in
              </button>
            </Stack>
          )}

          {step === 'sent' && (
            <Stack gap={4}>
              {/* Deliberately the same answer either way: a message that
                  varied would be a way to find out who has an account. */}
              <StatusBanner tone="info">{RESET_SENT}</StatusBanner>
              <Button
                variant="secondary"
                loading={sending}
                /* Deliberately not told whether it worked — see
                   `saidNothingOnPurpose`, and the banner above it. */
                onClick={() => saidNothingOnPurpose(forgot({ email: identifier.trim() }))}
              >
                Send it again
              </Button>
              <button
                type="button"
                className="self-start text-second text-action-text underline-offset-2 hover:underline"
                onClick={() => setStep('password')}
              >
                Back to sign in
              </button>
            </Stack>
          )}

          {/* While the link's own validity is still being read, show neither a form
              that might be about to become useless nor a flash of "expired" for a
              link that is actually fine. */}
          {step === 'choose' && link.isLoading && !link.isError && (
            <Stack className="items-center py-8">
              <Spinner label="Checking your link" />
            </Stack>
          )}

          {/* `link.isError` — the check itself could not be reached — is treated the
              same as a successful "valid": Save is still attempted, and the reset call
              it makes is the one answer that has always had to be right regardless of
              whether this earlier check could run at all. */}
          {step === 'choose' && (!link.isLoading || link.isError) && (
            <Stack as="form" gap={4} onSubmit={onSubmit(chooseNewPassword)}>
              <Text variant="body-strong" as="h2">
                Choose a new password
              </Text>
              <Text variant="secondary">{RESET_SIGNS_YOU_IN}</Text>
              <Field label="New password" hint={PASSWORD_RULE}>
                {(field) => (
                  <PasswordInput
                    {...field}
                    placeholder="Enter a new password"
                    autoComplete="new-password"
                    value={chosen.first}
                    onChange={(e) => setChosen({ ...chosen, first: e.target.value })}
                  />
                )}
              </Field>
              <Field label="And again">
                {({ id }) => (
                  <PasswordInput
                    placeholder="Enter the same password again"
                    id={id}
                    autoComplete="new-password"
                    value={chosen.second}
                    onChange={(e) => setChosen({ ...chosen, second: e.target.value })}
                  />
                )}
              </Field>
              {refused && <StatusBanner tone="error">{refused}</StatusBanner>}
              <Button
                type="submit"
                loading={resetting}
                disabled={chosen.first.length < 8 || chosen.second === ''}
              >
                Save
              </Button>
            </Stack>
          )}

          {step === 'expired' && (
            <Stack gap={4}>
              <Text variant="body-strong" as="h2">
                That link has expired
              </Text>
              {/* The rule, not just the failure. */}
              <Text variant="secondary">{LINK_EXPIRED}</Text>
              <Button onClick={() => setStep('forgot')}>Send a new link</Button>
              <button
                type="button"
                className="self-start text-second text-action-text underline-offset-2 hover:underline"
                onClick={() => setStep('password')}
              >
                Back to sign in
              </button>
            </Stack>
          )}

          {/* Lifted into its own component (`MfaEnrolment`) so a brand-new
              administrator accepting a team invitation goes through exactly the same
              screen, rather than a second implementation of it. */}
          {step === 'enrol' && enrolment && (
            <MfaEnrolment
              enrolment={enrolment}
              enrolStep={enrolStep}
              setEnrolStep={setEnrolStep}
              typing={typing}
              setTyping={setTyping}
              code={code}
              setCode={setCode}
              onSubmitCode={() => void submitCode()}
              confirming={confirming}
              refused={refused}
              // Only on the far side of a reset — see the same note on the code step.
              banner={justResetPassword ? PASSWORD_JUST_CHANGED : undefined}
            />
          )}

          {step === 'code' && (
            <Stack gap={4}>
              {/* Only on the far side of a reset — an ordinary sign-in's own code
                  step says nothing here, because nothing just changed. */}
              {justResetPassword && (
                <StatusBanner tone="success">{PASSWORD_JUST_CHANGED}</StatusBanner>
              )}
              <Text variant="body-strong" as="h2">
                The six digits from your authenticator app
              </Text>
              <CodeField
                value={code}
                onChange={setCode}
                onSubmit={() => void submitCode()}
                refused={refused}
                loading={verifying}
                label="Sign in"
              />
              <Text variant="caption" className="text-ink-muted">
                Lost your phone? A recovery code works here instead, once each.
              </Text>
              {/* And the way through when the authenticator and the codes are both
                  gone: a code to the email on the account, then set a new one up. */}
              <Button variant="tertiary" flush onClick={() => void startReset()}>
                Lost your authenticator?
              </Button>
            </Stack>
          )}

          {/* The email-code step of a reset. The code was sent when this step
              opened; the button below re-sends it. */}
          {step === 'reset' && (
            <Stack gap={4}>
              <Text variant="body-strong" as="h2">
                Set up a new authenticator
              </Text>
              <Text variant="caption" className="text-ink-muted">
                We have emailed a code to the address on your account. Enter it, and you will set up
                a new authenticator on the next screen. Your old one stops working.
              </Text>
              <CodeField
                value={code}
                onChange={setCode}
                onSubmit={() => void submitReset()}
                refused={refused}
                loading={resettingMfa}
                label="Continue"
              />
              <Row gap={2} className="items-center justify-between">
                <Button
                  variant="tertiary"
                  flush
                  disabled={askingReset}
                  onClick={() => saidNothingOnPurpose(requestReset({ challenge }))}
                >
                  Send the code again
                </Button>
                <Button
                  variant="ghost"
                  flush
                  onClick={() => {
                    setRefused(null)
                    setCode('')
                    setStep('code')
                  }}
                >
                  Back
                </Button>
              </Row>
            </Stack>
          )}
        </Stack>
      </Card>
    </AdminSignInLayout>
  )
}

/** Stops the browser navigating, and runs what the step does. */
function onSubmit(run: () => void): React.FormEventHandler<HTMLElement> {
  return (e) => {
    e.preventDefault()
    run()
  }
}
